'use client'

import ImageGallery from './imageGallery'
import { IngredientLocalAvailability } from './IngredientLocalAvailability'
import IngredientShareButton from './IngredientShareButton'
import { formatMonthRanges, isInSeasonNow } from '@/lib/seasonality'
import DOMPurify from 'isomorphic-dompurify'

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

const getSeasonalText = (isSeasonal?: boolean, availableMonths?: { month: number; isPeak?: boolean | null }[] | null) => {
  if (!isSeasonal || !availableMonths || availableMonths.length === 0) return null

  const months = availableMonths.map((m) => m.month)
  const peakMonths = availableMonths.filter((m) => m.isPeak).map((m) => MONTH_NAMES[m.month - 1])
  const rangeText = formatMonthRanges(months)

  if (peakMonths.length > 0) {
    return `In Season: ${rangeText} (Peak: ${peakMonths.join(', ')})`
  }
  return `In Season: ${rangeText}`
}

const IngredientIntro = ({ ingredient, hideShare = false }: any) => {
  const seasonalText = getSeasonalText(ingredient?.isSeasonal, ingredient?.availableMonths)
  const inSeasonNow = isInSeasonNow((ingredient?.availableMonths ?? []).map((m: any) => m.month))

  // Illustration is the hero; main photo is the secondary thumb when present.
  const images = [
    ...(ingredient?.illustrationImage ? [ingredient.illustrationImage] : []),
    ...(ingredient?.mainImage ? [ingredient.mainImage] : []),
  ]

  return (
    <div className="flex flex-col items-start gap-8 lg:flex-row lg:gap-12">
      <ImageGallery images={images} initialIndex={0} />

      <div className="flex w-full min-w-0 flex-col gap-4 lg:flex-1">
        <div className="flex items-start justify-between gap-4">
          <h1 className="min-w-0 flex-1 text-[36px] font-semibold leading-[130%] tracking-[-0.04em] text-[#202020]">
            {ingredient?.name}
          </h1>
          {!hideShare && <IngredientShareButton ingredientId={ingredient?.ingredient_id} />}
        </div>

        {ingredient?.description?.trim() ? (
          <div
            className="text-base leading-[150%] text-[#202020]"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(ingredient.description) }}
          />
        ) : (
          <p className="text-base leading-[150%] text-[#202020]">
            No description available for this ingredient.
          </p>
        )}

        {seasonalText && (
          <p className="flex flex-wrap items-center gap-2 text-base text-[#202020]">
            {seasonalText}
            {inSeasonNow && (
              <span className="text-sm font-semibold text-primary">● in season now</span>
            )}
          </p>
        )}

        <IngredientLocalAvailability
          ingredientName={ingredient?.name}
          listings={ingredient?.listings ?? []}
          parentName={ingredient?.parent_name}
          parentListings={ingredient?.parent_listings ?? []}
          childListings={ingredient?.child_listings ?? []}
        />
      </div>
    </div>
  )
}

export default IngredientIntro
